/** Wersja angielska — typowana względem polskiej (brak klucza = błąd kompilacji). */
import { fleetWebPl, type Shape } from './fleet-pl';

export const fleetWebEn: Shape<typeof fleetWebPl> = {
  errors: {
    invalid: 'Invalid data: {details}',
    invalidShort: 'Invalid data — check the form.',
    loadFailed: 'Could not load the data.',
    numberInvalid: 'Enter valid numbers (whole numbers where required).',
    required: 'Fill in all required fields.',
  },
  vehicles: {
    description: 'Fleet vehicles, odometer readings, drivers and lease mileage usage.',
    add: 'Add vehicle',
    searchPlaceholder: 'Search by plate, make, model…',
    filterStatus: 'Vehicle status',
    filterDriver: 'Driver',
    allStatuses: 'All statuses',
    allDrivers: 'All drivers',
    chipTotal: 'Vehicles',
    empty: 'No vehicles in the fleet yet.',
    emptyFiltered: 'No vehicle matches the filters.',
    noDriver: 'No driver',
    dueOverdue: 'Overdue',
    dueSoon: 'Due within 30 days',
    col: {
      vehicle: 'Vehicle',
      status: 'Status',
      driver: 'Driver',
      odometer: 'Mileage',
      lease: 'Lease limit',
      inspection: 'Inspection',
      insurance: 'Insurance',
    },
  },
  vehicleForm: {
    createTitle: 'New vehicle',
    editTitle: 'Edit vehicle',
    plate: 'Plate number',
    plateRequired: 'Enter the plate number.',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    type: 'Vehicle type',
    status: 'Status',
    vin: 'VIN',
    initialOdometer: 'Odometer (km)',
    initialOdometerHint: 'Saved as the first odometer reading.',
    group: 'Driver group',
    noGroup: 'No group',
    inspectionDueAt: 'Inspection due',
    insuranceDueAt: 'Insurance due',
    notes: 'Notes',
    created: 'Vehicle added.',
    updated: 'Vehicle saved.',
  },
  groups: {
    title: 'Driver groups',
    description: 'Groups organise vehicles and drivers (e.g. deliveries, sales).',
    name: 'Group name',
    descriptionField: 'Description (optional)',
    add: 'Add group',
    edit: 'Edit group',
    activate: 'Activate',
    deactivate: 'Disable',
    inactive: 'Inactive',
    vehicleCount: 'Vehicles: {count}',
    empty: 'No groups yet.',
    saved: 'Group saved.',
  },
  detail: {
    edit: 'Edit vehicle',
    vehicleData: 'Vehicle data',
    driverAndGroup: 'Driver and group',
    currentAssignment: 'Current assignment',
    noAssignment: 'No active assignment',
    since: 'since {date}',
    openLogbook: 'Open logbook',
    noOpenLogbook: 'None',
    activeLease: 'Active lease',
    routes: 'Linked routes',
    leasesHint: 'Lease contracts of this vehicle with mileage limit usage.',
    recentLogbooks: 'Recent logbooks',
    recentLogbooksHint: 'The five latest logbooks of this vehicle.',
    allLogbooks: 'All logbooks of the vehicle',
    noLogbooks: 'This vehicle has no logbooks yet.',
    tabs: {
      data: 'Details',
      odometer: 'Mileage',
      logbooks: 'Logbooks',
      leases: 'Leases',
      assignments: 'Assignments',
      attachments: 'Attachments',
    },
    fields: {
      plate: 'Plate number',
      vin: 'VIN',
      make: 'Make and model',
      year: 'Year',
      type: 'Type',
      status: 'Status',
      odometer: 'Odometer',
      inspection: 'Inspection until',
      insurance: 'Insurance until',
      defaultDriver: 'Default driver',
      group: 'Driver group',
      notes: 'Notes',
      createdAt: 'Added',
    },
  },
  odometer: {
    title: 'Odometer reading history',
    current: 'Current: {km}. Readings are the only source of mileage.',
    add: 'Add reading',
    correct: 'Correct odometer',
    empty: 'No odometer readings.',
    col: { date: 'Reading date', value: 'Odometer', source: 'Source', details: 'Details' },
    correction: 'Correction',
    replacement: 'Meter replacement',
    addTitle: 'New odometer reading',
    addDescription: 'Last reading: {km}. The value cannot be lower.',
    valueKm: 'Odometer (km)',
    source: 'Reading source',
    note: 'Note (optional)',
    saved: 'Odometer reading saved.',
    correctTitle: 'Correct or replace the odometer',
    correctDescription: 'Current value: {km}. The operation is written to the audit log.',
    newValueKm: 'New odometer value (km)',
    reason: 'Reason',
    reasonHint: 'Required — explain why the value is being changed.',
    isReplacement: 'Meter replacement',
    isReplacementHint: 'The new meter starts from a different value; mileage before and after is counted separately.',
    previousValue: 'Last value of the old meter (km)',
    previousHint: 'Empty = the vehicle’s last reading.',
    correctSubmit: 'Save correction',
    corrected: 'Odometer correction saved.',
  },
  assignments: {
    title: 'Driver assignments',
    description: 'Assignment history — a driver sees the vehicle during the assignment period.',
    assign: 'Assign driver',
    assignTitle: 'Assign a driver',
    assignDescription: 'An open-ended assignment in progress is closed the day before the new one starts.',
    driver: 'Driver',
    selectDriver: 'Select a driver',
    noDrivers: 'No accounts with the driver role.',
    validFrom: 'From',
    validTo: 'To (optional)',
    note: 'Note (optional)',
    current: 'Current',
    ongoing: 'open-ended',
    close: 'End',
    closeTitle: 'End assignment',
    closeDescription: 'The assignment of {name} will be closed on the chosen day.',
    closeDate: 'Last day of the assignment',
    empty: 'This vehicle has no assignments yet.',
    saved: 'Assignment saved.',
    closed: 'Assignment ended.',
    col: { driver: 'Driver', from: 'From', to: 'To', note: 'Note' },
  },
  attachments: {
    title: 'Contract attachments',
    upload: 'Add file',
    empty: 'No attachments.',
    noLeases: 'Attachments belong to lease contracts — this vehicle has no lease yet.',
    download: 'Download {name}',
    delete: 'Delete {name}',
    deleteConfirm: 'Delete the attachment “{name}”?',
    uploaded: 'Attachment added.',
    deleted: 'Attachment deleted.',
  },
  leases: {
    description: 'Lease contracts and mileage limit usage — warnings at thresholds, forecast to the end of the contract.',
    add: 'Add contract',
    edit: 'Edit contract',
    open: 'Open contract',
    openVehicle: 'Vehicle card',
    searchPlaceholder: 'Search by contract number, lessor, plate…',
    filterStatus: 'Contract status',
    filterWarning: 'Warning',
    allStatuses: 'All statuses',
    allWarnings: 'All levels',
    anyWarning: 'Any warning',
    chipTotal: 'Contracts',
    empty: 'No lease contracts.',
    emptyFiltered: 'No contract matches the filters.',
    noneOnVehicle: 'This vehicle has no lease contract.',
    limit: 'limit {km}',
    usageTitle: 'Limit usage',
    usageDescription: 'Mileage = difference of odometer readings since the contract start.',
    contractTitle: 'Contract details',
    period: 'Contract period',
    noRate: 'no rate',
  },
  usage: {
    usedPercent: '{percent}% of the limit used',
    thresholdsCaption: 'thresholds {values}%',
    annual: 'Annual limit (since the last contract anniversary)',
    remaining: 'Remaining limit',
    exceededBy: 'exceeded by {km}',
    avgMonthly: 'Monthly average',
    perMonth: 'mo.',
    daysRemaining: 'Until contract end',
    daysLeft: '{days} days (until {date})',
    forecastEnd: 'Forecast mileage at contract end',
    forecastOverage: 'Forecast overage',
    forecastCost: 'Forecast overage cost',
    costUnknown: 'unknown — no rate per km',
    noForecast: 'not enough data to forecast',
    withinLimit: 'within the limit',
    indicative: 'Indicative forecast',
    indicativeShort: 'indicative',
    indicativeHint: 'not enough data (short period or low mileage); the result may change a lot.',
    noMetrics: 'No limit calculation for this contract (inactive).',
  },
  leaseForm: {
    createTitle: 'New lease contract',
    editTitle: 'Edit lease contract',
    vehicle: 'Vehicle',
    selectVehicle: 'Select a vehicle',
    lessor: 'Lessor',
    contractNumber: 'Contract number',
    startDate: 'Contract start',
    endDate: 'Contract end',
    status: 'Contract status',
    totalLimitKm: 'Total limit (km)',
    annualLimitKm: 'Annual limit (km, optional)',
    startOdometerKm: 'Starting mileage (km)',
    startOdometerHint: 'Empty = the vehicle’s current odometer.',
    overageRate: 'Rate per excess km',
    overageRateHint: 'Without a rate the overage cost is not calculated.',
    monthlyFee: 'Monthly fee',
    currency: 'Currency',
    thresholds: 'Warning thresholds (%)',
    thresholdN: 'Threshold {n}',
    thresholdsHint: 'Three ascending values 1–100, defaulting to fleet settings (80 / 90 / 100).',
    settlementRules: 'Settlement rules',
    notes: 'Notes',
    attachment: 'Attachment (optional)',
    attachmentHint: 'Scan or contract file — more files can be added on the contract page.',
    created: 'Lease contract added.',
    updated: 'Contract saved.',
  },
  logbooks: {
    description: 'Logbook review queue: approve, return for correction or report a discrepancy.',
    mineDescription: 'Your logbooks — read-only view. Logbooks are kept in the mobile app.',
    chipTotal: 'Logbooks',
    searchPlaceholder: 'Search by plate, driver, purpose…',
    filterStatus: 'Logbook status',
    filterVehicle: 'Vehicle',
    filterDriver: 'Driver',
    filterPeriod: 'Period (month)',
    allStatuses: 'All statuses',
    allVehicles: 'All vehicles',
    allDrivers: 'All drivers',
    queue: 'To review',
    onlyFlagged: 'Flagged only',
    empty: 'No logbooks.',
    emptyFiltered: 'No logbook matches the filters — the review queue is empty.',
    planned: 'plan {km}',
    col: { date: 'Date', vehicle: 'Vehicle', driver: 'Driver', purpose: 'Purpose', km: 'Mileage', flags: 'Flags', status: 'Status' },
  },
  logbookDetail: {
    summary: 'Logbook summary',
    history: 'Event history',
    startKm: 'Odometer at start',
    endKm: 'Odometer at end',
    actualKm: 'Actual mileage',
    plannedKm: 'Planned mileage',
    startedAt: 'Started',
    endedAt: 'Ended',
    submittedAt: 'Submitted for review',
    flags: 'Flags',
    noFlags: 'none',
    driverNotes: 'Driver notes',
    reviewComment: 'Reviewer comment',
    comment: 'Comment',
    optional: 'optional',
    readOnlyHint: 'Read-only view of your logbook. Make changes in the mobile app.',
    correct: 'Correct odometer',
    correctTitle: 'Correct the odometer at the end of the logbook',
    correctDescription: 'Current end value: {km}. The change is audited and added to the logbook history.',
    newEndKm: 'New odometer at end (km)',
    corrected: 'Logbook reading corrected.',
    dialog: {
      approve: 'The logbook will be approved. The comment is optional.',
      return: 'The logbook goes back to the driver for correction. A comment is required.',
      discrepancy: 'The logbook will be marked as inconsistent. A comment is required.',
    },
    done: {
      approve: 'Logbook approved.',
      return: 'Logbook returned for correction.',
      discrepancy: 'Logbook marked as inconsistent.',
    },
  },
  settings: {
    description: 'Lease warning thresholds, minimum data for forecasts and logbook limits.',
    thresholdsTitle: 'Lease warning thresholds',
    thresholdsDescription: 'Three ascending mileage-limit usage thresholds (default 80 / 90 / 100%). They apply to new contracts.',
    thresholdN: 'Threshold {n}',
    forecastTitle: 'Minimum data for forecasts',
    forecastDescription: 'Below these values the lease forecast is marked as indicative.',
    minDays: 'Minimum contract days',
    minKm: 'Minimum kilometres driven',
    logbooksTitle: 'Logbooks',
    logbooksDescription: 'The limits drive automatic logbook flags (mileage above plan or above the daily maximum).',
    maxLogbookKm: 'Maximum logbook mileage (km)',
    tolerance: 'Plan tolerance (%)',
    rangeError: 'Value out of range (days 1–365, km 0–100,000, max logbook mileage 100–10,000, tolerance 0–200%).',
    reset: 'Restore saved',
    saved: 'Fleet settings saved.',
  },
};
